import { useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { getColor, formatMoney } from 'helpers/utils';
import * as echarts from 'echarts/core';
import { PieChart, BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { useSponsorships, useBeneficiaries } from 'hooks/useBeneficiaries';
import LoadingContainer from 'components/common/LoadingContainer';
import Flex from 'components/common/Flex';
import dayjs from 'dayjs';

// Register chart components
echarts.use([
  PieChart,
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  CanvasRenderer
]);

// Stats Card Component
const StatCard = ({ icon, title, value, subtitle, color = 'primary' }) => (
  <Card className="h-100">
    <Card.Body>
      <Flex justifyContent="between" alignItems="center">
        <div>
          <h6 className="text-700 mb-1">{title}</h6>
          <h2 className={`text-${color} mb-0`}>{value}</h2>
          {subtitle && <p className="text-500 fs--1 mb-0 mt-1">{subtitle}</p>}
        </div>
        <div className={`icon-circle icon-circle-${color}`}>
          <FontAwesomeIcon icon={icon} className="fs-2" />
        </div>
      </Flex>
    </Card.Body>
  </Card>
);

const SponsorshipReports = () => {
  const { sponsorships, isLoading: loadingSponsorships } = useSponsorships();
  const { beneficiaries, isLoading: loadingBeneficiaries } = useBeneficiaries();

  // Calculate stats
  const stats = useMemo(() => {
    if (!sponsorships || !beneficiaries) return null;

    // A sponsorship is active purely by its status. The old code checked a
    // non-existent `end_date`/`donar`/`amount` (leftover GraphQL field names),
    // so it never excluded ended rows and revenue was always 0.
    const activeSponsorships = sponsorships.filter(s => s.status === 'active');
    const endedSponsorships = sponsorships.filter(s => s.status === 'ended');
    const totalAllocated = activeSponsorships.reduce(
      (sum, s) => sum + (Number(s.allocated_amount) || 0),
      0
    );
    // A beneficiary is "sponsorable" only once it has a sponsorship type/cost
    // configured. Beneficiaries with no type can't receive a sponsor yet, so
    // they're excluded from Sponsored/Unsponsored and reported separately (this
    // is why Reports and the Management page previously disagreed).
    const sponsorable = beneficiaries.filter(
      b => (b.beneficiary_sponsorship_types || []).length > 0
    );
    const sponsoredBeneficiaries = new Set(
      activeSponsorships.map(s => s.orphan_id)
    ).size;
    const unsponsoredBeneficiaries = Math.max(
      0,
      sponsorable.length - sponsoredBeneficiaries
    );
    const notConfigured = beneficiaries.length - sponsorable.length;

    return {
      active: activeSponsorships.length,
      ended: endedSponsorships.length,
      totalAllocated,
      sponsored: sponsoredBeneficiaries,
      unsponsored: unsponsoredBeneficiaries,
      sponsorable: sponsorable.length,
      notConfigured,
      totalBeneficiaries: beneficiaries.length
    };
  }, [sponsorships, beneficiaries]);

  // Sponsored vs Unsponsored Pie Chart
  const sponsorshipPieOptions = useMemo(() => {
    if (!stats) return {};

    return {
      color: [getColor('success'), getColor('warning')],
      tooltip: {
        trigger: 'item',
        padding: [7, 10],
        backgroundColor: getColor('gray-100'),
        borderColor: getColor('gray-300'),
        textStyle: { color: getColor('dark') },
        borderWidth: 1,
        formatter: params => `<strong>${params.name}:</strong> ${params.value} (${params.percent}%)`
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        textStyle: {
          color: getColor('gray-700')
        }
      },
      series: [
        {
          name: 'Beneficiaries',
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['60%', '50%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderWidth: 2,
            borderColor: getColor('gray-100')
          },
          label: {
            show: true,
            position: 'outer',
            formatter: '{d}%'
          },
          data: [
            { value: stats.sponsored, name: 'Sponsored' },
            { value: stats.unsponsored, name: 'Unsponsored' }
          ]
        }
      ]
    };
  }, [stats]);

  // Location distribution bar chart
  const locationBarOptions = useMemo(() => {
    if (!beneficiaries) return {};

    const locationCount = beneficiaries.reduce((acc, b) => {
      const location = b.country || 'Unknown';
      acc[location] = (acc[location] || 0) + 1;
      return acc;
    }, {});

    const locations = Object.keys(locationCount).slice(0, 10);
    const counts = locations.map(loc => locationCount[loc]);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        padding: [7, 10],
        backgroundColor: getColor('gray-100'),
        borderColor: getColor('gray-300'),
        textStyle: { color: getColor('dark') },
        borderWidth: 1
      },
      xAxis: {
        type: 'category',
        data: locations,
        axisLabel: {
          rotate: 45,
          color: getColor('gray-600')
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: getColor('gray-600')
        }
      },
      series: [
        {
          data: counts,
          type: 'bar',
          itemStyle: {
            color: getColor('primary')
          }
        }
      ]
    };
  }, [beneficiaries]);

  // Monthly revenue trend
  const revenueTrendOptions = useMemo(() => {
    if (!sponsorships) return {};

    // Group by month
    const monthlyRevenue = {};
    const now = dayjs();

    // Last 6 months
    for (let i = 5; i >= 0; i--) {
      const month = now.subtract(i, 'month').format('MMM YYYY');
      monthlyRevenue[month] = 0;
    }

    sponsorships.forEach(s => {
      if (s.status === 'active' && s.start_date) {
        const startMonth = dayjs(s.start_date).format('MMM YYYY');
        if (Object.prototype.hasOwnProperty.call(monthlyRevenue, startMonth)) {
          monthlyRevenue[startMonth] += Number(s.allocated_amount) || 0;
        }
      }
    });

    const months = Object.keys(monthlyRevenue);
    const revenue = Object.values(monthlyRevenue);

    return {
      tooltip: {
        trigger: 'axis',
        padding: [7, 10],
        backgroundColor: getColor('gray-100'),
        borderColor: getColor('gray-300'),
        textStyle: { color: getColor('dark') },
        borderWidth: 1,
        formatter: params => `${params[0].name}<br/>${formatMoney(params[0].value)}`
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          color: getColor('gray-600')
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: getColor('gray-600'),
          formatter: value => formatMoney(value)
        }
      },
      series: [
        {
          data: revenue,
          type: 'line',
          smooth: true,
          itemStyle: {
            color: getColor('success')
          },
          areaStyle: {
            color: getColor('success'),
            opacity: 0.2
          }
        }
      ]
    };
  }, [sponsorships]);

  if (loadingSponsorships || loadingBeneficiaries) return <LoadingContainer />;

  return (
    <>
      {/* Beneficiaries with no sponsorship type can't be sponsored yet, so they
          are excluded from the Sponsored / Unsponsored figures below. Surface
          them here so the numbers are understood (and so Reports lines up with
          the Sponsorship Management page). */}
      {stats?.notConfigured > 0 && (
        <Card className="mb-3 bg-soft-info">
          <Card.Body>
            <Flex alignItems="center">
              <div className="icon-circle icon-circle-info me-3">
                <FontAwesomeIcon icon="info-circle" className="fs-2" />
              </div>
              <div>
                <h5 className="mb-1">
                  {stats.notConfigured} beneficiaries not set up for sponsorship
                </h5>
                <p className="mb-0 text-700 fs--1">
                  They have no sponsorship type or cost defined yet, so they
                  can&apos;t receive a sponsor until configured — and are
                  excluded from the Sponsored / Unsponsored figures below (which
                  cover the {stats.sponsorable} sponsorable beneficiaries out of{' '}
                  {stats.totalBeneficiaries} total).
                </p>
              </div>
            </Flex>
          </Card.Body>
        </Card>
      )}

      {/* Stats Cards */}
      <Row className="g-3 mb-3">
        <Col md={6} lg={3}>
          <StatCard
            icon="check-circle"
            title="Active Sponsorships"
            value={stats?.active || 0}
            subtitle={`${formatMoney(stats?.totalAllocated || 0)} allocated`}
            color="success"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            icon="clock"
            title="Ended Sponsorships"
            value={stats?.ended || 0}
            subtitle="No longer active"
            color="secondary"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            icon="user-check"
            title="Sponsored Beneficiaries"
            value={stats?.sponsored || 0}
            subtitle={`of ${stats?.sponsorable || 0} sponsorable`}
            color="primary"
          />
        </Col>
        <Col md={6} lg={3}>
          <StatCard
            icon="user-times"
            title="Unsponsored"
            value={stats?.unsponsored || 0}
            subtitle="Need sponsors"
            color="danger"
          />
        </Col>
      </Row>

      {/* Charts */}
      <Row className="g-3">
        {/* Sponsorship Distribution */}
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Sponsorship Distribution</h5>
            </Card.Header>
            <Card.Body>
              <ReactEChartsCore
                echarts={echarts}
                option={sponsorshipPieOptions}
                style={{ height: '300px' }}
              />
            </Card.Body>
          </Card>
        </Col>

        {/* Location Distribution */}
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Beneficiaries by Location</h5>
            </Card.Header>
            <Card.Body>
              <ReactEChartsCore
                echarts={echarts}
                option={locationBarOptions}
                style={{ height: '300px' }}
              />
            </Card.Body>
          </Card>
        </Col>

        {/* Revenue Trend */}
        <Col xs={12}>
          <Card>
            <Card.Header>
              <h5 className="mb-0">Monthly Revenue Trend (Last 6 Months)</h5>
            </Card.Header>
            <Card.Body>
              <ReactEChartsCore
                echarts={echarts}
                option={revenueTrendOptions}
                style={{ height: '300px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <style>{`
        .icon-circle {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.15;
        }
        .icon-circle-primary { background-color: var(--falcon-primary); }
        .icon-circle-success { background-color: var(--falcon-success); }
        .icon-circle-warning { background-color: var(--falcon-warning); }
        .icon-circle-danger { background-color: var(--falcon-danger); }
      `}</style>
    </>
  );
};

export default SponsorshipReports;
